import {createSmokingSession} from './session.js';
import {createSmokingItem,orderSmokingItems} from './item.js';
import {createItemPreparation} from './preparation.js';
import {createSessionWood,orderSessionWoods} from './wood-mix.js';
import {createSmokingStage,orderSmokingStages} from './stage.js';
import {createStageItemTarget,createStageTarget} from './stage-target.js';

export const LIVE_START_PLAN_VERSION=1;
const STARTABLE_STATUSES=new Set(['planned','preparation','ready']);

function deepFreeze(value){
  if(!value||typeof value!=='object'||Object.isFrozen(value))return value;
  for(const child of Object.values(value))deepFreeze(child);
  return Object.freeze(value);
}

function assertSameOwner(value,session,label){
  if(value.userId!==session.userId)throw new TypeError(label+' owner mismatch');
}

function assertSameSession(value,session,label){
  if(value.sessionId!==session.id)throw new TypeError(label+' session mismatch');
}

function activeItems(values=[]){
  const items=orderSmokingItems(values);
  return items.map(value=>createSmokingItem(value));
}

function activeWoods(values=[]){
  const woods=orderSessionWoods(values);
  return woods.map(value=>createSessionWood(value));
}

function activeStages(values=[]){
  const stages=orderSmokingStages(values);
  return stages.map(value=>createSmokingStage(value));
}

export function createLiveStartPlan({
  session,
  items=[],
  preparations=[],
  woods=[],
  stages=[],
  stageTargets=[],
  itemTargets=[]
}={}){
  const current=createSmokingSession(session);
  if(!STARTABLE_STATUSES.has(current.status)){
    throw new TypeError('Session must be Planned, Preparation or Ready before Live handoff');
  }

  const plannedItems=activeItems(items);
  const plannedStages=activeStages(stages);
  const plannedWoods=activeWoods(woods);

  if(!plannedItems.length)throw new TypeError('Live handoff requires at least one active item');
  if(!plannedStages.length)throw new TypeError('Live handoff requires at least one active stage');

  for(const item of plannedItems){
    assertSameOwner(item,current,'Item');
    assertSameSession(item,current,'Item');
  }
  for(const wood of plannedWoods){
    assertSameOwner(wood,current,'Wood');
    assertSameSession(wood,current,'Wood');
  }
  for(const stage of plannedStages){
    assertSameOwner(stage,current,'Stage');
    assertSameSession(stage,current,'Stage');
  }

  const itemIds=new Set(plannedItems.map(item=>item.id));
  const stageIds=new Set(plannedStages.map(stage=>stage.id));

  const preparationByItem=new Map();
  for(const raw of preparations||[]){
    const value=createItemPreparation(raw);
    assertSameOwner(value,current,'Preparation');
    if(!itemIds.has(value.itemId))throw new TypeError('Preparation references an inactive or foreign item');
    if(preparationByItem.has(value.itemId))throw new TypeError('Duplicate preparation for item');
    preparationByItem.set(value.itemId,value);
  }

  const targetByStage=new Map();
  for(const raw of stageTargets||[]){
    const value=createStageTarget(raw);
    assertSameOwner(value,current,'Stage target');
    if(!stageIds.has(value.stageId))throw new TypeError('Stage target references an inactive or foreign stage');
    if(targetByStage.has(value.stageId))throw new TypeError('Duplicate stage target');
    targetByStage.set(value.stageId,value);
  }

  const itemTargetsByStage=new Map();
  const seenItemTargets=new Set();
  for(const raw of itemTargets||[]){
    const value=createStageItemTarget(raw);
    assertSameOwner(value,current,'Stage item target');
    if(!stageIds.has(value.stageId))throw new TypeError('Item target references an inactive or foreign stage');
    if(!itemIds.has(value.itemId))throw new TypeError('Item target references an inactive or foreign item');
    const key=value.stageId+':'+value.itemId;
    if(seenItemTargets.has(key))throw new TypeError('Duplicate stage item target');
    seenItemTargets.add(key);
    if(!itemTargetsByStage.has(value.stageId))itemTargetsByStage.set(value.stageId,[]);
    itemTargetsByStage.get(value.stageId).push(value);
  }

  const result={
    version:LIVE_START_PLAN_VERSION,
    session:{
      id:current.id,
      userId:current.userId,
      status:current.status,
      title:current.title,
      plannedStartAt:current.plannedStartAt,
      plannedEndAt:current.plannedEndAt,
      smokerId:current.smokerId,
      smokerSnapshot:current.smokerSnapshot
    },
    items:plannedItems.map(item=>({
      id:item.id,
      catalogueProductCode:item.catalogueProductCode,
      customProductId:item.customProductId,
      nameSnapshot:item.nameSnapshot,
      quantity:item.quantity,
      mass:item.mass,
      massUnit:item.massUnit,
      sortOrder:item.sortOrder,
      preparation:preparationByItem.get(item.id)||null
    })),
    woods:plannedWoods.map(wood=>({
      id:wood.id,
      sourceKind:wood.sourceKind,
      woodKey:wood.woodKey,
      inventoryItemId:wood.inventoryItemId,
      nameSnapshot:wood.nameSnapshot,
      sharePercent:wood.sharePercent,
      sortOrder:wood.sortOrder
    })),
    stages:plannedStages.map(stage=>({
      id:stage.id,
      typeKey:stage.typeKey,
      nameOverride:stage.nameOverride,
      sortOrder:stage.sortOrder,
      target:targetByStage.get(stage.id)||null,
      itemTargets:[...(itemTargetsByStage.get(stage.id)||[])]
        .sort((a,b)=>String(a.itemId).localeCompare(String(b.itemId)))
    }))
  };

  return deepFreeze(result);
}
